<script setup lang="ts">
import { computed } from 'vue'

const props = withDefaults(
  defineProps<{
    /** `default`, the caption row above a body · `lead`, a mark, a title and a note heading the part below · `sub`, a caption row between two bodies · `bar`, a right-aligned row for a control or a strip of figures of the caller's own in the default slot. */
    variant?: 'default' | 'lead' | 'sub' | 'bar'
    /** The caption; in `lead`, the title. Left out when a `lead` rim carries its own block in the default slot. */
    title?: string
    note?: string
    /** The title's element; `span` when the rim sits inside a button, such as a disclosure trigger. */
    headingLevel?: 'h2' | 'h3' | 'span'
    /** Sets the caption in the primary ink at medium weight, for a rim that is a section's trigger. */
    strong?: boolean
    /** Pins the rim, as its panel's head, to the top of the scroller; it takes over the panel's top inset so it pins at the height it rests at. */
    sticky?: boolean
  }>(),
  {
    variant: 'default',
    title: undefined,
    note: undefined,
    headingLevel: 'h2',
    strong: false,
    sticky: false,
  },
)

defineSlots<{
  /** `lead` and `bar` only: a block of the caller's own, such as a summary, a field or a control, in place of the title and note. */
  default?: () => unknown
  /** `lead`, a mark before the title · `default`, an icon before the caption when the rim has no `meta`, `mark` or `action`. */
  leading?: () => unknown
  /** `default` and `sub`: a small mark right after the caption, such as the logo of the AI engine it reads. */
  mark?: () => unknown
  /** A caption-size count or note. It rides beside the caption when there is an action, else at the right end. */
  meta?: () => unknown
  /** One `sm` ghost `AppButton`. It hangs 8px into the right padding, so its label lines up with the body edge. */
  action?: () => unknown
}>()

const titleClass = computed(() =>
  props.strong ? 'text-body-sm-medium text-text-primary' : 'text-body-sm text-text-secondary',
)

const ROOT_CLASSES = {
  default: 'justify-between gap-8 px-8 py-4',
  sub: 'justify-between gap-8 px-8 py-4 after-well:py-6',
  bar: 'justify-end gap-8 px-8 py-4',
  lead: 'justify-start gap-6 pt-6 pr-8 pb-8 pl-6 after-well:pt-8',
} as const

const STICKY_HEAD = 'sticky-top -mt-2 min-h-(--panel-head-height) bg-inherit pt-6'
</script>

<template>
  <div
    class="flex min-w-0 items-center"
    :class="[ROOT_CLASSES[variant], sticky ? STICKY_HEAD : 'min-h-(--panel-rim-height)']"
    :data-scroll-head="sticky || undefined"
  >
    <template v-if="variant === 'lead'">
      <slot name="leading" />
      <div v-if="$slots.default" class="min-w-0 flex-1">
        <slot />
      </div>
      <div v-else class="flex min-w-0 flex-1 flex-col">
        <component :is="headingLevel" class="min-w-0 text-h3-medium text-text-primary">
          {{ title }}
        </component>
        <p v-if="note" class="min-w-0 text-body-sm text-text-secondary">{{ note }}</p>
      </div>
    </template>

    <slot v-else-if="variant === 'bar'" />

    <template v-else>
      <span v-if="$slots.leading" class="flex min-w-0 items-center gap-4">
        <slot name="leading" />
        <component :is="headingLevel" :class="titleClass" class="min-w-0">
          {{ title }}
        </component>
      </span>
      <span v-else-if="$slots.action && $slots.meta" class="flex min-w-0 items-baseline gap-4">
        <component :is="headingLevel" :class="titleClass" class="min-w-0">
          {{ title }}
        </component>
        <span class="min-w-0 text-caption text-text-tertiary"><slot name="meta" /></span>
      </span>
      <span v-else-if="$slots.mark" class="flex min-w-0 items-center gap-4">
        <component :is="headingLevel" :class="titleClass" class="min-w-0">
          {{ title }}
        </component>
        <slot name="mark" />
      </span>
      <component :is="headingLevel" v-else :class="titleClass" class="min-w-0">
        {{ title }}
      </component>
      <span
        v-if="!$slots.action && $slots.meta"
        class="min-w-0 text-right text-caption text-text-tertiary"
      >
        <slot name="meta" />
      </span>
    </template>

    <div v-if="$slots.action" class="-me-4 flex shrink-0 items-center">
      <slot name="action" />
    </div>
  </div>
</template>
